﻿import { LoadingFieldSkeleton } from 'components/secondaryMarket/shared';
import { CompactSelector } from 'components/shared/compact-selector';
import { useFormatSalesCounterpartTooltip } from 'components/shared/compact-selector/use-format-tooltip';
import { useSalesCounterpartButton } from 'components/shared/compact-selector/use-toggle-button';
import { OtcSearchResult } from 'components/shared/OtcClientSearch/OtcSearchResult';
import { SelectSearch } from 'components/shared/SelectSearch/SelectSearch';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { OtcClient } from 'state/secondaryMarket/shared/referential/referential.model';
import type { LoadingStatus } from 'utils/status';

type OtcClientSelectorProps = {
  selectedClientEliotCode?: string;
  productOtcClients: ReadonlyArray<OtcClient> | undefined;
  otcClientLoadingStatus: LoadingStatus;
  isDisabled?: boolean;
  onClientChanged: (client: ISalesCounterpart) => void;
  onClientClear: () => void;
  getErrors?: (item: OtcClient) => string[];
  className?: string;
  onOpenChange?: (isOpen: boolean) => void;
  isReadonly?: boolean;
};

export const OtcClientWarning: FC<{ selectedOtcClient: OtcClient }> = ({ selectedOtcClient }) => {
  const { formatMessage, formatNumber } = useIntl();
  const way = useMemo(() => {
    switch (selectedOtcClient?.way) {
      case 'SG_BUY':
        return formatMessage({ id: 'secondaryMarket.productSummary.label.sgBuy' });
      case 'SG_SELL':
        return formatMessage({ id: 'secondaryMarket.productSummary.label.sgSell' });
    }
  }, [selectedOtcClient, formatMessage]);
  const remaining = formatNumber(selectedOtcClient?.remaining ?? 0);

  return (
    <small className="text-info fs-6 d-block mt-1 ">
      {way} {remaining}
    </small>
  );
};

export const OtcClientSelector: FC<OtcClientSelectorProps> = ({
  otcClientLoadingStatus: loadingStatus,
  productOtcClients,
  selectedClientEliotCode,
  isDisabled,
  onClientChanged,
  onClientClear,
  className,
  getErrors,
  isReadonly,
  onOpenChange,
}) => {
  const selectedOtcClient = useMemo(
    () => productOtcClients?.find(x => x?.eliotCode === selectedClientEliotCode),
    [productOtcClients, selectedClientEliotCode],
  );
  const { label, className: buttonClassName } = useSalesCounterpartButton(selectedOtcClient, true);
  const tooltip = useFormatSalesCounterpartTooltip(selectedOtcClient);
  if (loadingStatus === 'loading') {
    return <LoadingFieldSkeleton className="flex-grow-1" />;
  }

  return (
    <>
      <CompactSelector<OtcClient>
        isReadonly={isReadonly}
        onOpenChange={onOpenChange}
        selectedValue={selectedOtcClient}
        isDisabled={isDisabled}
        className={className}
        hasClearButton={false}
        hasBorder
        tooltip={tooltip}
        onSelectedItemChanged={client => {
          if (client) {
            onClientChanged(client);
          } else {
            onClientClear();
          }
        }}
        renderButton={(ref, onClick) => (
          <CompactSelector.ToggleButton
            id="otc-client"
            e2ePrefix="client"
            ref={ref}
            isReadonly={isReadonly}
            onClick={onClick}
            label={label}
            className={buttonClassName}
          />
        )}
        renderContent={onSelectedChanged => (
          <SelectSearch<OtcClient>
            disabled={isDisabled}
            syncItems={productOtcClients}
            onValueSelected={onSelectedChanged}
            resultRenderer={OtcSearchResult}
            hasSearchInput={false}
            deduplicateComparator={(client1, client2) => client1.eliotCode === client2.eliotCode}
            getErrorForItem={getErrors}
          ></SelectSearch>
        )}
      ></CompactSelector>

      {selectedOtcClient && <OtcClientWarning selectedOtcClient={selectedOtcClient} />}
    </>
  );
};

OtcClientSelector.displayName = 'OtcClientSelector';
